/**
 * SIGN UP COMPONENT
 * =================
 * 
 * User registration form for new users to create accounts.
 * Includes comprehensive form validation, terms acceptance, and 
 * navigation to related pages. Features the custom brand logo
 * for consistent branding throughout the authentication flow.
 * 
 * FEATURES:
 * - Complete registration form with validation
 * - Password strength requirements
 * - Terms and privacy policy acceptance
 * - Navigation to sign-in page
 * - Responsive design with proper spacing
 * - Custom brand logo integration
 * - Loading states and error handling
 */

import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { ArrowLeft, Eye, EyeOff, Check, X } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../src/hooks/useAuth";
import { getPasswordStrength, MIN_PASSWORD_STRENGTH_SCORE } from "../src/lib/validations";

interface SignUpProps {
  onSignUp: () => void;                              // Called when registration is successful
  onNavigateToSignIn: () => void;                    // Navigate to sign-in page
  onBackToLanding: () => void;                       // Navigate back to landing page
}

export function SignUp({ 
  onSignUp, 
  onNavigateToSignIn, 
  onBackToLanding 
}: SignUpProps) {
  
  // AUTHENTICATION HOOK
  // ===================
  const { signUp, signInWithGoogle, verifyEmailOtp, resendVerificationEmail } = useAuth();
  
  // FORM STATE MANAGEMENT
  // =====================
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [monthlyReports, setMonthlyReports] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // Set once the account exists but still needs email verification.
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpNotice, setOtpNotice] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    terms?: string;
    general?: string;
  }>({});

  const passwordStrength = getPasswordStrength(formData.password);

  // FORM VALIDATION
  // ===============
  const validateForm = () => {
    const newErrors: typeof errors = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (passwordStrength.score < MIN_PASSWORD_STRENGTH_SCORE) {
      newErrors.password = "Password is too weak. Please meet more requirements.";
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // Terms acceptance validation
    if (!acceptTerms) {
      newErrors.terms = "You must accept the terms and privacy policy";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // FORM SUBMISSION
  // ===============
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const { error, needsEmailVerification } = await signUp(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName
      );

      if (error?.code === 'user_already_exists' || error?.code === 'email_exists') {
        // Most often an earlier sign-up that created the account but was never
        // verified. Send a fresh code and take them to the verification step
        // rather than leaving them stuck on an error they cannot act on.
        const { error: resendError } = await resendVerificationEmail(formData.email);
        if (resendError) {
          setErrors({
            general: "An account with this email already exists. Try signing in instead."
          });
        } else {
          setOtpNotice("That email is already registered but not verified. We sent a new code.");
          setVerificationEmail(formData.email);
        }
      } else if (error) {
        setErrors({
          general: error.message || "An error occurred while creating your account. Please try again."
        });
      } else if (needsEmailVerification) {
        // Account created, but the project verifies at sign-up so there is no
        // session yet. Staying on this page would invite a resubmit, which
        // fails as "user already exists".
        setVerificationEmail(formData.email);
      } else {
        // Success - redirect to sign in or show confirmation
        onSignUp();
      }

    } catch (error) {
      setErrors({
        general: "An unexpected error occurred. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  // INPUT CHANGE HANDLER
  // ====================
  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // EMAIL VERIFICATION
  // ==================
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationEmail || !otp.trim()) return;

    setOtpLoading(true);
    setOtpError(null);
    setOtpNotice(null);

    try {
      const { error, signedIn } = await verifyEmailOtp(verificationEmail, otp.trim());

      if (error) {
        setOtpError(error.message || "That code wasn't accepted. Request a new one and try again.");
      } else if (signedIn) {
        // A session came back, so the auth listener will pick it up.
        onSignUp();
      } else {
        // Verified, but the provider issued no session; signing in now works.
        onNavigateToSignIn();
      }
    } catch {
      setOtpError("An unexpected error occurred. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!verificationEmail) return;

    setResendLoading(true);
    setOtpError(null);
    setOtpNotice(null);

    try {
      const { error } = await resendVerificationEmail(verificationEmail);
      if (error) {
        setOtpError(error.message || "Couldn't send a new code. Please try again shortly.");
      } else {
        setOtpNotice("We sent a new code. It may take a moment to arrive.");
      }
    } catch {
      setOtpError("An unexpected error occurred. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  // GOOGLE SIGN UP
  // ==============
  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    setErrors({});

    try {
      const { error } = await signInWithGoogle();
      
      if (error) {
        setErrors({
          general: error.message || "Failed to sign up with Google. Please try again."
        });
      }
    } catch (error) {
      setErrors({
        general: "An unexpected error occurred with Google sign-up."
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  // ACCOUNT CREATED, VERIFICATION PENDING
  // =====================================
  // The account exists at this point; only the session is missing. Showing the
  // form again would just produce a "user already exists" failure, so collect
  // the emailed code here instead.
  if (verificationEmail) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="flex justify-center">
            <Logo size="lg" onClick={onBackToLanding} showText={false} />
          </div>
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle>Confirm your email</CardTitle>
              <CardDescription>
                We sent a verification code to{" "}
                <span className="font-medium text-foreground">{verificationEmail}</span>.
                Enter it below to finish creating your account.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                {otpError && (
                  <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                    {otpError}
                  </div>
                )}
                {otpNotice && (
                  <div className="p-3 text-sm bg-muted border rounded-md">
                    {otpNotice}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="otp">Verification code</Label>
                  <Input
                    id="otp"
                    name="otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value);
                      setOtpError(null);
                    }}
                    disabled={otpLoading}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={otpLoading || !otp.trim()}>
                  {otpLoading ? "Verifying..." : "Verify email"}
                </Button>

                <div className="text-center text-sm text-muted-foreground">
                  Didn't get it?{" "}
                  <button
                    type="button"
                    className="text-primary hover:underline disabled:opacity-50"
                    onClick={handleResendOtp}
                    disabled={resendLoading}
                  >
                    {resendLoading ? "Sending..." : "Send a new code"}
                  </button>
                </div>

                <div className="text-center text-sm text-muted-foreground">
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={onNavigateToSignIn}
                  >
                    Back to sign in
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">

        {/* HEADER WITH LOGO AND BACK BUTTON */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onBackToLanding}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </Button>
            <div className="flex-1 flex justify-center">
              <Logo 
                size="lg" 
                onClick={onBackToLanding}
                showText={false}
              />
            </div>
            <div className="w-16"></div> {/* Spacer for balance */}
          </div>
          
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Create your account
            </h1>
            <p className="text-sm text-muted-foreground">
              Join Decisionr.xyz to start improving your decision making
            </p>
          </div>
        </div>

        {/* SIGN UP FORM */}
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle>Sign up</CardTitle>
            <CardDescription>
              Create your account to get started with better decision making
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-4">
              
              {/* GOOGLE SIGN UP BUTTON */}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleSignUp}
                disabled={isLoading || googleLoading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {googleLoading ? "Signing up..." : "Continue with Google"}
              </Button>

              {/* SEPARATOR */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              
              {/* GENERAL ERROR MESSAGE */}
              {errors.general && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                  {errors.general}
                </div>
              )}

              {/* NAME FIELDS */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={errors.firstName ? "border-destructive" : ""}
                    disabled={isLoading}
                  />
                  {errors.firstName && (
                    <p className="text-xs text-destructive">{errors.firstName}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={errors.lastName ? "border-destructive" : ""}
                    disabled={isLoading}
                  />
                  {errors.lastName && (
                    <p className="text-xs text-destructive">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* EMAIL FIELD */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={errors.email ? "border-destructive" : ""}
                  disabled={isLoading}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              {/* PASSWORD FIELD */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a strong password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={errors.password ? "border-destructive pr-10" : "pr-10"}
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                
                {/* PASSWORD REQUIREMENTS */}
                {formData.password && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className={`flex items-center space-x-1 ${passwordStrength.requirements.length ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordStrength.requirements.length ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>8+ characters</span>
                      </div>
                      <div className={`flex items-center space-x-1 ${passwordStrength.requirements.uppercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordStrength.requirements.uppercase ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>Uppercase</span>
                      </div>
                      <div className={`flex items-center space-x-1 ${passwordStrength.requirements.lowercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordStrength.requirements.lowercase ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>Lowercase</span>
                      </div>
                      <div className={`flex items-center space-x-1 ${passwordStrength.requirements.number ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordStrength.requirements.number ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>Number</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>

              {/* CONFIRM PASSWORD FIELD */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={errors.confirmPassword ? "border-destructive pr-10" : "pr-10"}
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                )}
              </div>

              {/* TERMS ACCEPTANCE */}
              <div className="space-y-2">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={acceptTerms}
                    onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
                    className="mt-0.5"
                  />
                  <Label htmlFor="terms" className="text-sm leading-relaxed">
                    I agree to the{" "}
                    <button type="button" className="text-primary hover:underline">
                      Terms of Service
                    </button>{" "}
                    and{" "}
                    <button type="button" className="text-primary hover:underline">
                      Privacy Policy
                    </button>
                  </Label>
                </div>
                {errors.terms && (
                  <p className="text-sm text-destructive">{errors.terms}</p>
                )}
              </div>

              {/* MONTHLY REPORT OPT-IN */}
              <div className="space-y-2">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="monthlyReports"
                    checked={monthlyReports}
                    onCheckedChange={(checked) => setMonthlyReports(checked as boolean)}
                    className="mt-0.5"
                  />
                  <Label htmlFor="monthlyReports" className="text-sm leading-relaxed">
                    Send me a monthly report with insights about my decision-making progress
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground ml-6">
                  You can change this preference anytime in Settings
                </p>
              </div>

              {/* SUBMIT BUTTON */}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
              >
                {isLoading ? "Creating account..." : "Create account"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* SIGN IN LINK */}
        <div className="text-center text-sm">
          <span className="text-muted-foreground">Already have an account? </span>
          <Button
            variant="link"
            size="sm"
            onClick={onNavigateToSignIn}
            className="px-0"
          >
            Sign in
          </Button>
        </div>
      </div>
    </div>
  );
}